/**
 * AD → Organizations → Coaches (contacts-table style, click-to-edit).
 * Per-sport tabs (org order) → one table grouped by team level (website level
 * names). Writes team staff_positions SOT via /api/ad-staff. Coach WP pushes
 * season-gated (held icon+tooltip).
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Loader2, Users } from 'lucide-react';
import {
  API_URL, useExistingStaff, InlineText, PersonCell, ExpandChevron, HeadshotBio, HeldBadge,
  RemoveBtn, SortHeader, useSort, SearchBox, matchesSearch, TableFooter, AddRoleRow, thCls, tdCls,
} from './adStaffShared';

const COLS = 8; // chevron|Role|Person|Email|Phone|Prev|Status|remove

export default function OrgCoachesPanel({ orgId, isDark }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const people = useExistingStaff(orgId, reloadKey);
  const [sportTab, setSportTab] = useState('');
  const [expanded, setExpanded] = useState({});
  const [q, setQ] = useState('');
  const { sort, onSort, sortCards } = useSort();

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try { const { data } = await axios.get(`${API_URL}/api/ad-staff/${orgId}/coaches`); setData(data.data); setSportTab(p => p || data.data?.sports?.[0]?.code || ''); }
    catch (e) { /* noop */ } finally { setLoading(false); }
  }, [orgId]);
  useEffect(() => { load(); }, [load, reloadKey]);
  const reload = () => setReloadKey(k => k + 1);
  const toggle = (id) => setExpanded(s => ({ ...s, [id]: !s[id] }));

  const changePerson = async (card, coach_id) => { await axios.post(`${API_URL}/api/ad-staff/${orgId}/assign`, { position_id: card.position_id, person: coach_id }); reload(); };
  const removePerson = async (card) => { if (!window.confirm(`Remove ${card.person?.name || ''} from ${card.role_title}?`)) return; await axios.post(`${API_URL}/api/ad-staff/${orgId}/unfill`, { position_id: card.position_id }); reload(); };
  const saveField = async (card, field, val) => { if (!card.person?.coach_id) return; await axios.post(`${API_URL}/api/ad-staff/${orgId}/coach`, { coach_id: card.person.coach_id, [field]: val }); reload(); };
  const addCoach = (sportName, level) => async ({ role, coach_id }) => { await axios.post(`${API_URL}/api/ad-staff/${orgId}/assign`, { scope: 'team', sport: sportName, level, role, person: coach_id }); reload(); };

  if (loading || !data) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>;

  const sports = data.sports || [];
  const active = sports.find(s => s.code === sportTab) || sports[0];
  const rowCls = isDark ? 'group border-t border-slate-700/50 hover:bg-slate-700/20' : 'group border-t border-slate-100 hover:bg-slate-50';
  const total = active ? active.level_groups.reduce((n, lg) => n + lg.cards.length, 0) : 0;

  return (
    <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
      <CardContent className="p-4 space-y-3" data-testid="org-coaches-panel">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className={`flex items-center gap-2 text-base font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}><Users className="h-5 w-5 text-amber-500" /> Coaches</div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="coaches-fill-contacts" title="Fill blank emails / phones from the Family Directory (unique name matches; never overwrites)"
              onClick={async () => { try { const { data } = await axios.post(`${API_URL}/api/ad-staff/${orgId}/coaches/fill-contacts`); toast.success(data.filled.length ? `Filled ${data.filled.length} coaches from the Family Directory` : 'Nothing new in the Family Directory'); reload(); } catch (e) { toast.error(e?.response?.data?.detail || 'Could not fill contacts'); } }}>
              Fill from Family Directory
            </Button>
            <SearchBox value={q} onChange={setQ} isDark={isDark} />
          </div>
        </div>

        <CoachPolicyToggles orgId={orgId} isDark={isDark} />

        <div className="flex flex-wrap gap-1" data-testid="coaches-sport-tabs">
          {sports.map(s => <Button key={s.code} size="sm" variant={active?.code === s.code ? 'default' : 'outline'} className="h-8 text-xs" onClick={() => setSportTab(s.code)} data-testid={`coaches-sport-${s.code}`}>{s.name}</Button>)}
        </div>

        {!active || active.level_groups.length === 0 ? (
          <p className={`text-sm italic ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>No teams for this sport yet — add teams in the Teams tab first.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead><tr>
                <th className={thCls(isDark) + ' w-5'}></th>
                <SortHeader label="Role" sortKey="role" sort={sort} onSort={onSort} isDark={isDark} />
                <SortHeader label="Person" sortKey="person" sort={sort} onSort={onSort} isDark={isDark} />
                <th className={thCls(isDark)}>Email</th><th className={thCls(isDark)}>Phone</th>
                <th className={thCls(isDark)}>Previous holder</th>
                <th className={thCls(isDark) + ' w-10'}>Status</th><th className={thCls(isDark) + ' w-6'}></th>
              </tr></thead>
              <tbody>
                {active.level_groups.map(lg => {
                  const cards = sortCards(lg.cards.filter(c => matchesSearch(c, q)));
                  return (
                    <React.Fragment key={lg.group_slug}>
                      <tr data-testid={`coaches-level-${lg.group_slug}`}>
                        <td colSpan={COLS} className={`px-2 pt-2.5 pb-0.5 text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{lg.level_label}</td>
                      </tr>
                      {cards.map(card => (
                        <React.Fragment key={card.slug}>
                          <tr className={rowCls} data-testid={`row-${card.slug}`}>
                            <td className={tdCls}><ExpandChevron open={!!expanded[card.position_id]} onClick={() => toggle(card.position_id)} testid={`exp-${card.slug}`} /></td>
                            <td className={tdCls + ' font-medium ' + (isDark ? 'text-amber-400' : 'text-amber-700')}>{card.role_title}</td>
                            <td className={tdCls + ' min-w-[170px]'}><PersonCell card={card} people={people} orgId={orgId} isDark={isDark} testid={`person-${card.slug}`} onPick={({ coach_id }) => changePerson(card, coach_id)} /></td>
                            <td className={tdCls + ' min-w-[150px]'}><InlineText value={card.person?.email} canEdit={!!card.person} isDark={isDark} type="email" suggested={card.person?.email_suggested} testid={`email-${card.slug}`} onSave={v => saveField(card, 'email', v)} /></td>
                            <td className={tdCls + ' min-w-[120px]'}><InlineText value={card.person?.phone} canEdit={!!card.person} isDark={isDark} suggested={card.person?.phone_suggested} testid={`phone-${card.slug}`} onSave={v => saveField(card, 'phone', v)} /></td>
                            <td className={tdCls + ' text-xs ' + (isDark ? 'text-slate-500' : 'text-slate-400')} data-testid={`prev-${card.slug}`}>{card.prior_holder?.name ? `${card.prior_holder.name}${card.prior_holder.season ? ` (${card.prior_holder.season})` : ''}` : '—'}</td>
                            <td className={tdCls + ' text-center'}><HeldBadge card={card} sportName={active.name} /></td>
                            <td className={tdCls}><RemoveBtn onClick={() => removePerson(card)} testid={`remove-${card.slug}`} /></td>
                          </tr>
                          {expanded[card.position_id] && <tr className={isDark ? 'bg-slate-800/40' : 'bg-slate-50'}><td></td><td className="px-2 py-1" colSpan={COLS - 1}><HeadshotBio card={card} orgId={orgId} isDark={isDark} onDone={reload} /></td></tr>}
                        </React.Fragment>
                      ))}
                      <AddRoleRow roles={data.team_roles || []} people={people} orgId={orgId} isDark={isDark} colSpan={COLS} onCreate={addCoach(active.name, lg.level_code)} allowNewRole testid={`add-${lg.group_slug}`} />
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <TableFooter count={total} isDark={isDark} />
      </CardContent>
    </Card>
  );
}

// Org-wide coach policies (repointed from the removed AD Settings ▸ Coaches
// section, migration_log_0147). Persist on org.settings; no new endpoints.
function CoachPolicyToggles({ orgId, isDark }) {
  const [crossTeam, setCrossTeam] = useState(false);
  const [asstRestricted, setAsstRestricted] = useState(false);
  useEffect(() => {
    if (!orgId) return;
    axios.get(`${API_URL}/api/organizations/${orgId}`).then(({ data }) => {
      const s = data.settings || {};
      setCrossTeam(!!s.coaches_cross_team_visibility);
      setAsstRestricted(!!s.assistant_coach_restricted);
    }).catch(() => {});
  }, [orgId]);
  const flip = async (key, cur, setter) => {
    try { await axios.put(`${API_URL}/api/organizations/${orgId}`, { settings: { [key]: !cur } }); setter(!cur); }
    catch { /* noop */ }
  };
  const box = isDark ? 'bg-slate-700/30 border-slate-600' : 'bg-slate-50 border-slate-200';
  const title = isDark ? 'text-white' : 'text-slate-900';
  const label = isDark ? 'text-slate-400' : 'text-slate-500';
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="coach-policies">
      <div className={`flex items-center justify-between p-3 rounded-lg border ${box}`}>
        <div className="pr-2">
          <p className={`text-sm font-medium ${title}`}>Cross-Team Visibility</p>
          <p className={`text-xs ${label}`}>Allow coaches to view other teams' rosters and schedules</p>
        </div>
        <Button size="sm" variant={crossTeam ? 'default' : 'outline'}
          className={crossTeam ? 'bg-green-600 hover:bg-green-700' : isDark ? 'border-slate-600 text-slate-300' : ''}
          onClick={() => flip('coaches_cross_team_visibility', crossTeam, setCrossTeam)} data-testid="cross-team-visibility-toggle">
          {crossTeam ? 'Enabled' : 'Disabled'}
        </Button>
      </div>
      <div className={`flex items-center justify-between p-3 rounded-lg border ${box}`}>
        <div className="pr-2">
          <p className={`text-sm font-medium ${title}`}>Restrict Assistant Coaches</p>
          <p className={`text-xs ${label}`}>Limit assistant coaches to roster view only (no schedule or payment access)</p>
        </div>
        <Button size="sm" variant={asstRestricted ? 'default' : 'outline'}
          className={asstRestricted ? 'bg-amber-600 hover:bg-amber-700' : isDark ? 'border-slate-600 text-slate-300' : ''}
          onClick={() => flip('assistant_coach_restricted', asstRestricted, setAsstRestricted)} data-testid="assistant-coach-restricted-toggle">
          {asstRestricted ? 'Restricted' : 'Unrestricted'}
        </Button>
      </div>
    </div>
  );
}
